"use client"

import { useState, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Slider } from "@/components/ui/slider"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Copy, RefreshCw, Trash2, Check, CheckCircle2, XCircle } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { toast } from "sonner"

type IdFormat = "uuid-v4" | "uuid-v7" | "uuid-v1-like" | "ulid" | "nanoid" | "short-id" | "numeric"

interface FormatOption {
  value: IdFormat
  label: string
  description: string
}

const formatOptions: FormatOption[] = [
  { value: "uuid-v4", label: "UUID v4", description: "Random UUID (standard)" },
  { value: "uuid-v7", label: "UUID v7", description: "Timestamp-sortable UUID (RFC 9562)" },
  { value: "uuid-v1-like", label: "UUID v1-like", description: "Timestamp-based UUID" },
  { value: "ulid", label: "ULID", description: "Sortable unique ID" },
  { value: "nanoid", label: "NanoID", description: "Compact URL-safe ID" },
  { value: "short-id", label: "Short ID", description: "8-character alphanumeric" },
  { value: "numeric", label: "Numeric ID", description: "Random numeric string" },
]

// UUID v4 (random)
function generateUUIDv4(): string {
  return crypto.randomUUID()
}

// UUID v1-like (timestamp-based, not RFC compliant but similar structure)
function generateUUIDv1Like(): string {
  const now = Date.now()
  const timeHex = now.toString(16).padStart(12, "0")
  const randomHex = () => Math.floor(Math.random() * 16).toString(16)
  const randomPart = Array.from({ length: 20 }, randomHex).join("")

  return [
    timeHex.slice(0, 8),
    timeHex.slice(8, 12),
    "1" + randomPart.slice(0, 3),
    (8 + Math.floor(Math.random() * 4)).toString(16) + randomPart.slice(3, 6),
    randomPart.slice(6, 18),
  ].join("-")
}

// UUID v7 (Unix timestamp-based, sortable, RFC 9562)
function generateUUIDv7(): string {
  const now = Date.now()
  const timeHex = now.toString(16).padStart(12, "0")
  const randomBytes = new Uint8Array(10)
  crypto.getRandomValues(randomBytes)

  // Set version 7 (0111) in the ver field
  randomBytes[0] = (randomBytes[0] & 0x0f) | 0x70
  // Set variant 10 in the variant field
  randomBytes[2] = (randomBytes[2] & 0x3f) | 0x80

  const randHex = Array.from(randomBytes, (b) => b.toString(16).padStart(2, "0")).join("")

  return [
    timeHex.slice(0, 8),
    timeHex.slice(8, 12),
    randHex.slice(0, 4),
    randHex.slice(4, 8),
    randHex.slice(8, 20),
  ].join("-")
}

// ULID (Universally Unique Lexicographically Sortable Identifier)
const ULID_CHARS = "0123456789ABCDEFGHJKMNPQRSTVWXYZ"
function generateULID(): string {
  const now = Date.now()
  let timeStr = ""
  let t = now
  for (let i = 0; i < 10; i++) {
    timeStr = ULID_CHARS[t % 32] + timeStr
    t = Math.floor(t / 32)
  }

  let randomStr = ""
  for (let i = 0; i < 16; i++) {
    randomStr += ULID_CHARS[Math.floor(Math.random() * 32)]
  }

  return timeStr + randomStr
}

// NanoID (compact URL-safe)
const NANOID_CHARS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-_"
function generateNanoID(size: number = 21): string {
  let id = ""
  for (let i = 0; i < size; i++) {
    id += NANOID_CHARS[Math.floor(Math.random() * 64)]
  }
  return id
}

// Short ID (alphanumeric)
const SHORT_CHARS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz"
function generateShortID(size: number = 8): string {
  let id = ""
  for (let i = 0; i < size; i++) {
    id += SHORT_CHARS[Math.floor(Math.random() * 62)]
  }
  return id
}

// Numeric ID
function generateNumericID(size: number = 12): string {
  let id = ""
  for (let i = 0; i < size; i++) {
    id += Math.floor(Math.random() * 10).toString()
  }
  return id
}

function generateId(format: IdFormat): string {
  switch (format) {
    case "uuid-v4":
      return generateUUIDv4()
    case "uuid-v7":
      return generateUUIDv7()
    case "uuid-v1-like":
      return generateUUIDv1Like()
    case "ulid":
      return generateULID()
    case "nanoid":
      return generateNanoID()
    case "short-id":
      return generateShortID()
    case "numeric":
      return generateNumericID()
    default:
      return generateUUIDv4()
  }
}

function formatOutput(id: string, uppercase: boolean, noHyphens: boolean, addBraces: boolean): string {
  let result = id
  if (noHyphens) {
    result = result.replace(/-/g, "")
  }
  if (uppercase) {
    result = result.toUpperCase()
  } else {
    result = result.toLowerCase()
  }
  if (addBraces) {
    result = `{${result}}`
  }
  return result
}

function validateUuid(input: string): { valid: boolean; version: string | null; variant: string | null } {
  const cleaned = input.replace(/[{}]/g, "").trim()
  const uuidRegex = /^[0-9a-f]{8}-?[0-9a-f]{4}-?([0-9a-f])[0-9a-f]{3}-?([0-9a-f])[0-9a-f]{3}-?[0-9a-f]{12}$/i
  const match = uuidRegex.exec(cleaned)

  if (!match) return { valid: false, version: null, variant: null }

  const versionChar = match[1]
  const variantChar = parseInt(match[2], 16)

  let version: string | null = null
  if (["1", "2", "3", "4", "5", "6", "7"].includes(versionChar)) {
    version = `v${versionChar}`
  }

  let variant: string | null = null
  if ((variantChar & 0x8) === 0) variant = "NCS"
  else if ((variantChar & 0xc) === 0x8) variant = "RFC 4122"
  else if ((variantChar & 0xe) === 0xc) variant = "Microsoft"
  else variant = "Future"

  return { valid: true, version, variant }
}

export function UuidGenerator() {
  const [ids, setIds] = useState<string[]>([])
  const [count, setCount] = useState(1)
  const [format, setFormat] = useState<IdFormat>("uuid-v4")
  const [uppercase, setUppercase] = useState(false)
  const [noHyphens, setNoHyphens] = useState(false)
  const [addBraces, setAddBraces] = useState(false)
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null)
  const [validateInput, setValidateInput] = useState("")
  const [validationResult, setValidationResult] = useState<{ valid: boolean; version: string | null; variant: string | null } | null>(null)
  const [activeTab, setActiveTab] = useState("generate")

  const generateIds = useCallback(() => {
    const newIds = Array.from({ length: count }, () => {
      const id = generateId(format)
      return formatOutput(id, uppercase, noHyphens, addBraces)
    })
    setIds(newIds)
    toast.success(`Generated ${count} ID${count > 1 ? "s" : ""}`)
  }, [count, format, uppercase, noHyphens, addBraces])

  const copyToClipboard = useCallback((text: string, index: number) => {
    navigator.clipboard.writeText(text)
    setCopiedIndex(index)
    setTimeout(() => setCopiedIndex(null), 2000)
    toast.success("Copied to clipboard")
  }, [])

  const copyAll = useCallback(() => {
    navigator.clipboard.writeText(ids.join("\n"))
    toast.success("All IDs copied to clipboard")
  }, [ids])

  const clear = useCallback(() => {
    setIds([])
  }, [])

  const selectedFormat = formatOptions.find((f) => f.value === format)

  const handleValidate = useCallback(() => {
    if (!validateInput.trim()) {
      toast.error("Enter a UUID to validate")
      return
    }
    setValidationResult(validateUuid(validateInput))
  }, [validateInput])

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Generate Unique IDs</CardTitle>
          <CardDescription>
            Generate UUIDs, ULIDs, NanoIDs, and other unique identifiers
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="generate">Generate</TabsTrigger>
              <TabsTrigger value="validate">Validate</TabsTrigger>
            </TabsList>

            <TabsContent value="validate" className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label>UUID to validate</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="e.g., 550e8400-e29b-41d4-a716-446655440000"
                    value={validateInput}
                    onChange={(e) => {
                      setValidateInput(e.target.value)
                      setValidationResult(null)
                    }}
                    className="font-mono text-sm"
                  />
                  <Button onClick={handleValidate}>Validate</Button>
                </div>
              </div>
              {validationResult && (
                <div className={`p-4 rounded-lg border ${validationResult.valid ? "bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800" : "bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800"}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {validationResult.valid ? (
                      <>
                        <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                        <span className="font-medium text-green-800 dark:text-green-200">Valid UUID</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                        <span className="font-medium text-red-800 dark:text-red-200">Invalid UUID</span>
                      </>
                    )}
                  </div>
                  {validationResult.valid && (
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Version:</span>
                        <span className="font-mono">{validationResult.version || "Unknown"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Variant:</span>
                        <span className="font-mono">{validationResult.variant || "Unknown"}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="generate" className="space-y-4 pt-2">
          {/* Format Selection */}
          <div className="space-y-2">
            <Label>ID Format</Label>
            <Select value={format} onValueChange={(v) => setFormat(v as IdFormat)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {formatOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex flex-col">
                      <span>{option.label}</span>
                      <span className="text-xs text-muted-foreground">{option.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedFormat && (
              <p className="text-sm text-muted-foreground">{selectedFormat.description}</p>
            )}
          </div>

          {/* Options */}
          <div className="flex flex-wrap gap-4">
            {(format === "uuid-v4" || format === "uuid-v7" || format === "uuid-v1-like") && (
              <>
                <div className="flex items-center gap-2">
                  <Switch id="uppercase" checked={uppercase} onCheckedChange={setUppercase} />
                  <Label htmlFor="uppercase">Uppercase</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch id="no-hyphens" checked={noHyphens} onCheckedChange={setNoHyphens} />
                  <Label htmlFor="no-hyphens">No hyphens</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch id="braces" checked={addBraces} onCheckedChange={setAddBraces} />
                  <Label htmlFor="braces">Add braces</Label>
                </div>
              </>
            )}
          </div>

          {/* Count Slider */}
          <div className="space-y-2">
            <Label>Number of IDs: {count}</Label>
            <Slider
              value={[count]}
              onValueChange={([value]) => setCount(value)}
              min={1}
              max={100}
              step={1}
              className="w-full"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button onClick={generateIds} className="flex-1">
              <RefreshCw className="w-4 h-4 mr-2" />
              Generate
            </Button>
            {ids.length > 0 && (
              <>
                <Button variant="outline" onClick={copyAll}>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy All
                </Button>
                <Button variant="outline" onClick={clear}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Generated IDs */}
      {ids.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Generated IDs ({ids.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {ids.map((id, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 bg-muted rounded-md font-mono text-sm group"
                >
                  <span className="break-all">{id}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => copyToClipboard(id, index)}
                    className="ml-2 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    {copiedIndex === index ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Format Reference */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">ID Format Reference</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-3">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium">UUID v4</h4>
              <p className="text-muted-foreground text-xs">Standard random UUID</p>
              <code className="text-xs">550e8400-e29b-41d4-a716-446655440000</code>
            </div>
            <div>
              <h4 className="font-medium">UUID v7</h4>
              <p className="text-muted-foreground text-xs">Timestamp-sortable, RFC 9562</p>
              <code className="text-xs">01924fb3-3c6e-7a4b-8d1f-2e3b4c5d6e7f</code>
            </div>
            <div>
              <h4 className="font-medium">ULID</h4>
              <p className="text-muted-foreground text-xs">Sortable, 26 chars, Crockford Base32</p>
              <code className="text-xs">01ARZ3NDEKTSV4RRFFQ69G5FAV</code>
            </div>
            <div>
              <h4 className="font-medium">NanoID</h4>
              <p className="text-muted-foreground text-xs">Compact, URL-safe, 21 chars</p>
              <code className="text-xs">V1StGXR8_Z5jdHi6B-myT</code>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
